/**
 * PRD.227 — Add a billing adjustment (Debit or Credit).
 * PRD.242 — Auto mode: enter a desired outstanding balance and let the
 *           system resolve it to a stored Debit or Credit.
 *
 * Clinic-facing semantics:
 *   - Debit  → adds to patient balance
 *   - Credit → reduces patient balance
 *   - Auto   → UI-only entry convenience; the user types the desired
 *              outstanding balance and we compute the delta against the
 *              current outstanding, persisting a normal debit/credit.
 *
 * V1 fields: mode · amount/desired · reason · note · optional invoice link.
 * Payments stay separate from adjustments (PRD §5.3); this dialog
 * never touches `invoice_payments`. The shared patient billing
 * aggregate folds in debit/credit automatically via SQL. Auto mode
 * introduces NO new stored adjustment type (PRD.242 §5.5/§8.1).
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoicesByPatient,
  CREDIT_REASONS,
  DEBIT_REASONS,
  type AdjustmentType,
  type InvoiceRow,
} from '@helios/data-access';
import { isPositive, parseAmountInput, phpFromCents, toCents } from '@helios/domain';
import { useFormatMoney, useRegionalSettings } from '../../hooks/useFormatMoney';
import { usePatientBilling } from './usePatientBilling';

// PRD.242 — UI entry modes. `auto` is a clinic-friendly convenience that
// resolves to a stored debit/credit; it is NEVER persisted as a type.
type AdjustmentMode = 'auto' | 'debit' | 'credit';

// ─── Visit Date helpers (PRD.229) ───

function todayISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function visitDateToIso(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  orgId: string;
  /**
   * When set, the dialog opens pre-scoped to a specific invoice (the
   * invoice picker becomes "Linked invoice" with this id selected and
   * disabled). Used from invoice detail surfaces.
   */
  prefilledInvoiceId?: string | null;
  onRecorded?: () => void;
}

export function AddAdjustmentDialog({
  open,
  onOpenChange,
  patientId,
  orgId,
  prefilledInvoiceId,
  onRecorded,
}: Props) {
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  // For the optional invoice picker. Only loaded when the dialog is
  // open to avoid the cost on idle patient pages.
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    open && patientId
      ? qInvoicesByPatient(patientId)
      : {
          key: ['noop-patient-invoices'] as const,
          sql: 'SELECT NULL WHERE 0',
          params: [],
          watch: [],
        },
  );

  // PRD.242 — current outstanding drives the Auto-mode delta. The patient
  // balance reflects every adjustment regardless of invoice link, so it is
  // the correct "current outstanding" for both patient-wide and
  // invoice-scoped entry points.
  const billing = usePatientBilling(open ? patientId : null);

  // PRD.242 — `mode` is the UI entry mode (Auto default). `amountInput` is
  // used by the explicit Debit/Credit modes; `desiredInput` by Auto.
  const [mode, setMode] = useState<AdjustmentMode>('auto');
  const [amountInput, setAmountInput] = useState('');
  const [desiredInput, setDesiredInput] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [invoiceId, setInvoiceId] = useState<string>('');
  // PRD.229 — Visit Date anchors `adjusted_at` to clinic context. Defaults today.
  const [visitDate, setVisitDate] = useState<string>(() => todayISODate());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the dialog reopens.
  useEffect(() => {
    if (!open) return;
    setMode('auto');
    setAmountInput('');
    setDesiredInput('');
    setReason('');
    setNote('');
    setInvoiceId(prefilledInvoiceId ?? '');
    setVisitDate(todayISODate());
    setSubmitting(false);
    setError(null);
  }, [open, prefilledInvoiceId]);

  const parsed = useMemo(() => parseAmountInput(amountInput), [amountInput]);
  const desiredParsed = useMemo(() => parseAmountInput(desiredInput), [desiredInput]);
  const trimmedReason = reason.trim();

  const currentOutstandingCents = billing.balanceCents;

  // PRD.242 §6 — resolve the Auto-mode desired outstanding into a concrete
  // debit/credit. `delta = desired − current`: positive → debit, negative →
  // credit, zero → no-op (blocked). Only meaningful in Auto mode.
  const autoResult = useMemo(() => {
    if (mode !== 'auto' || desiredParsed == null) return null;
    const desiredCents = toCents(desiredParsed);
    const delta = desiredCents - currentOutstandingCents;
    if (delta === 0) {
      return { type: null as AdjustmentType | null, cents: 0, isNoOp: true };
    }
    return {
      type: (delta > 0 ? 'debit' : 'credit') as AdjustmentType,
      cents: Math.abs(delta),
      isNoOp: false,
    };
  }, [mode, desiredParsed, currentOutstandingCents]);

  // The adjustment type that will actually be stored. In explicit modes it is
  // the mode itself; in Auto it is whatever the delta resolves to.
  const effectiveType: AdjustmentType | null = mode === 'auto' ? (autoResult?.type ?? null) : mode;
  const reasonChips = effectiveType === 'debit' ? DEBIT_REASONS : CREDIT_REASONS;

  const canSubmit =
    !!db &&
    !!patientId &&
    !submitting &&
    trimmedReason.length > 0 &&
    (mode === 'auto'
      ? !billing.isLoading && autoResult != null && !autoResult.isNoOp
      : parsed != null && isPositive(parsed));

  async function handleSubmit() {
    if (!db || !patientId) return;

    // PRD.242 — resolve the persisted type + amount from the active mode.
    // Auto never stores a third type; it collapses to debit/credit here.
    let persistType: AdjustmentType;
    let cents: number;
    if (mode === 'auto') {
      if (desiredParsed == null) {
        setError('Enter a desired outstanding balance.');
        return;
      }
      if (!autoResult || autoResult.isNoOp || autoResult.type == null) {
        setError('Desired outstanding equals current outstanding — no adjustment is needed.');
        return;
      }
      persistType = autoResult.type;
      cents = autoResult.cents;
    } else {
      if (!parsed || !isPositive(parsed)) {
        setError('Amount must be greater than zero.');
        return;
      }
      persistType = mode;
      cents = toCents(parsed);
    }

    if (!trimmedReason) {
      setError('Reason is required.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      // PRD.229 — adjusted_at is anchored to the user-picked Visit Date.
      const adjustedAt = visitDateToIso(visitDate);
      const selectedInvoiceId = invoiceId || null;
      // Best-effort encounter link: if the user picked an invoice, copy
      // its encounter_id so adjustment history aligns with that visit's
      // billing context. Pure convenience; patient-level adjustments
      // leave encounter_id NULL.
      const linkedInvoice = selectedInvoiceId
        ? invoiceRows.find((inv) => inv.id === selectedInvoiceId)
        : null;
      const encounterId = linkedInvoice?.encounter_id ?? null;

      const adjustmentId = crypto.randomUUID();
      await db.execute(
        `INSERT INTO billing_adjustments
           (id, org_id, patient_id, invoice_id, encounter_id,
            adjustment_type, amount_cents, reason, note,
            adjusted_at, created_at, updated_at, created_by, updated_by)
         VALUES (?, ?, ?, ?, ?,  ?, ?, ?, ?,  ?, ?, ?, ?, ?)`,
        [
          adjustmentId,
          orgId,
          patientId,
          selectedInvoiceId,
          encounterId,
          persistType,
          cents,
          trimmedReason,
          note.trim() || null,
          adjustedAt,
          now,
          now,
          user?.uid ?? null,
          user?.uid ?? null,
        ],
      );

      // PRD.243 — reflect the adjustment into Treatment Record as a
      // visibility/history entry. billing_adjustments stays the source of
      // truth; this row is supplemental (kind='adjustment', zero charged/paid
      // so it never feeds Treatment Record totals) and links back via
      // billing_adjustment_id / invoice_id / encounter_id. Best-effort: the
      // authoritative write above already succeeded, so a reflection failure
      // must not surface as a save error.
      try {
        const reflectionLabel = persistType === 'debit' ? 'Debit Adjustment' : 'Credit Adjustment';
        const reflectionNote = note.trim() ? `${trimmedReason} — ${note.trim()}` : trimmedReason;
        await db.execute(
          `INSERT INTO patient_treatment_records
             (id, org_id, clinic_id, patient_id, encounter_id, invoice_id,
              kind, billing_adjustment_id, date, procedure_name,
              amount_charged_cents, amount_paid_cents, notes, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?,  ?, ?, ?, ?,  ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            null,
            patientId,
            encounterId,
            selectedInvoiceId,
            'adjustment',
            adjustmentId,
            visitDate,
            reflectionLabel,
            0,
            0,
            reflectionNote,
            now,
            now,
          ],
        );
      } catch (reflectErr) {
        console.error('[AddAdjustmentDialog] treatment-record reflection failed', reflectErr);
      }

      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('[AddAdjustmentDialog] insert failed', e);
      setError('Could not save adjustment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Billing Adjustment"
      description="Auto sets the balance to a target you choose. Debit adds to the patient's balance; Credit reduces it. Payments are recorded separately."
      maxWidth={480}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save Adjustment'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        {/* PRD.229 — Visit Date, always shown. Defaults to today, drives adjusted_at. */}
        <Field label="Visit Date">
          <input
            type="date"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value || todayISODate())}
            style={inputStyle()}
          />
        </Field>

        {/* Mode — segmented (PRD.242: Auto · Debit · Credit, Auto default) */}
        <Field label="Mode">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 'var(--sp-2)',
              padding: 3,
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <SegmentedButton
              active={mode === 'auto'}
              onClick={() => {
                setMode('auto');
                setReason('');
                setError(null);
              }}
              tone="primary"
              title="Auto"
              subtitle="set balance"
            />
            <SegmentedButton
              active={mode === 'debit'}
              onClick={() => {
                setMode('debit');
                setReason('');
                setError(null);
              }}
              tone="warning"
              title="Debit"
              subtitle="adds to balance"
            />
            <SegmentedButton
              active={mode === 'credit'}
              onClick={() => {
                setMode('credit');
                setReason('');
                setError(null);
              }}
              tone="success"
              title="Credit"
              subtitle="reduces balance"
            />
          </div>
        </Field>

        {/* PRD.242 — Auto mode: desired outstanding + resolved preview */}
        {mode === 'auto' && (
          <>
            <Field label="Current Outstanding">
              <div style={readonlyValueStyle()}>
                {billing.isLoading ? '—' : fmt(phpFromCents(currentOutstandingCents))}
              </div>
            </Field>

            <Field label="Desired Outstanding">
              <div style={{ position: 'relative' }}>
                <span style={currencyPrefixStyle()}>{currencySymbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={desiredInput}
                  onChange={(e) => {
                    setDesiredInput(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="0.00"
                  style={inputStyle(24)}
                  autoFocus
                />
              </div>
            </Field>

            <Field label="Resulting Adjustment">
              <div style={readonlyValueStyle()}>
                {desiredParsed == null ? (
                  <span style={{ color: 'var(--text-muted)' }}>
                    Enter a desired outstanding to preview.
                  </span>
                ) : autoResult?.isNoOp ? (
                  <span style={{ color: 'var(--text-muted)' }}>
                    No change — desired matches current outstanding.
                  </span>
                ) : autoResult ? (
                  <span>
                    <strong
                      style={{
                        color:
                          autoResult.type === 'debit'
                            ? 'var(--color-warning-text)'
                            : 'var(--color-success-text)',
                      }}
                    >
                      {autoResult.type === 'debit' ? 'Debit' : 'Credit'}{' '}
                      {fmt(phpFromCents(autoResult.cents))}
                    </strong>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {autoResult.type === 'debit'
                        ? ' — adds to patient balance'
                        : ' — reduces patient balance'}
                    </span>
                  </span>
                ) : null}
              </div>
            </Field>
          </>
        )}

        {/* Amount — explicit Debit/Credit modes only */}
        {mode !== 'auto' && (
          <Field label="Amount">
            <div style={{ position: 'relative' }}>
              <span style={currencyPrefixStyle()}>{currencySymbol}</span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="0.00"
                style={inputStyle(24)}
                autoFocus
              />
            </div>
            {parsed && isPositive(parsed) && (
              <HintRow>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  {mode === 'debit' ? 'Adds ' : 'Reduces '}
                  <strong style={{ color: 'var(--text)' }}>{fmt(parsed)}</strong>
                  {mode === 'debit' ? ' to patient balance' : ' from patient balance'}
                </span>
              </HintRow>
            )}
          </Field>
        )}

        {/* Reason */}
        <Field label="Reason">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={effectiveType === 'debit' ? 'e.g. Missed charge' : 'e.g. Returned item'}
            style={inputStyle()}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 6,
            }}
          >
            {reasonChips.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setReason(suggestion)}
                style={chipStyle(reason === suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </Field>

        {/* Optional invoice link */}
        <Field label="Linked invoice (optional)">
          <select
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            disabled={!!prefilledInvoiceId}
            style={inputStyle()}
          >
            <option value="">— Patient-level (no invoice link) —</option>
            {invoiceRows.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {(inv.invoice_no ?? inv.id.slice(0, 8)) +
                  (inv.date_iso ? ` · ${inv.date_iso}` : '') +
                  (inv.status ? ` · ${inv.status}` : '')}
              </option>
            ))}
          </select>
          <HintRow>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
              Linking is optional — the patient balance reflects the adjustment either way.
            </span>
          </HintRow>
        </Field>

        {/* Note */}
        <Field label="Note (optional)">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Returned 2x Amoxicillin per Dr. Cruz"
            style={inputStyle()}
          />
        </Field>

        {error && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger-text, #b91c1c)',
              background: 'var(--color-danger-bg, #fef2f2)',
              border: '1px solid var(--color-danger-border, #fecaca)',
              padding: 'var(--sp-2) var(--sp-3)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Small layout helpers ───

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: 'var(--text-muted)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HintRow({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 4 }}>{children}</div>;
}

function SegmentedButton({
  active,
  onClick,
  tone,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  tone: 'primary' | 'warning' | 'success';
  title: string;
  subtitle: string;
}) {
  const TONE_BG: Record<typeof tone, string> = {
    primary: 'var(--color-primary-bg, #eef2ff)',
    warning: 'var(--color-warning-bg)',
    success: 'var(--color-success-bg)',
  };
  const TONE_FG: Record<typeof tone, string> = {
    primary: 'var(--color-primary, #4f46e5)',
    warning: 'var(--color-warning-text)',
    success: 'var(--color-success-text)',
  };
  const activeBg = TONE_BG[tone];
  const activeFg = TONE_FG[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? activeBg : 'transparent',
        color: active ? activeFg : 'var(--text-muted)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        padding: '10px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)' }}>{title}</span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 500,
          opacity: active ? 0.85 : 1,
        }}
      >
        {subtitle}
      </span>
    </button>
  );
}

function inputStyle(paddingLeft = 12): React.CSSProperties {
  return {
    width: '100%',
    height: 36,
    padding: `0 12px 0 ${paddingLeft}px`,
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
  };
}

/** PRD.242 — read-only value row (Current Outstanding, Resulting Adjustment). */
function readonlyValueStyle(): React.CSSProperties {
  return {
    width: '100%',
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface-2)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
  };
}

/** Currency symbol prefix overlaid on an amount input. */
function currencyPrefixStyle(): React.CSSProperties {
  return {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--text-muted)',
    fontSize: 'var(--font-size-sm)',
    pointerEvents: 'none',
  };
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    height: 26,
    padding: '0 10px',
    borderRadius: 'var(--radius-full)',
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--border)',
    background: active ? 'var(--color-primary-bg, #eef2ff)' : 'var(--bg-surface)',
    color: active ? 'var(--color-primary, #4f46e5)' : 'var(--text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}
